/**
 * Modal para ver y editar todos los logros de un miembro
 * Incluye especialidades, maestrías, certificaciones y clases progresivas
 */

import { useState, useMemo } from "react";
import { Button, Badge, SpecialtyImage } from "@/components/ui";
import {
  X,
  Loader2,
  Award,
  Trophy,
  GraduationCap,
  Medal,
  User,
  ChevronDown,
  ChevronRight,
  Plus,
  Edit2,
} from "lucide-react";
import {
  useAchievementsByPerson,
  useClassProgressByPerson,
} from "@/hooks/useProgress";
import {
  useSpecialties,
  useMaestrias,
  useCertificaciones,
} from "@/hooks/useCatalogs";
import EditAchievementModal from "./EditAchievementModal";
import ClassProgressModal from "./ClassProgressModal";
import type {
  MemberLegacy as Member,
  Achievement,
  ClassProgress,
} from "@/types";

interface MemberProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: Member;
  clubId: string;
  periodId: string;
  onAddAchievement: () => void;
  onAddClass: () => void;
}

const classStatusLabels: Record<string, string> = {
  in_progress: "En progreso",
  completed: "Completada",
  pending_approval: "Pendiente",
  approved: "Aprobada",
  rejected: "Rechazada",
  invested: "Investido",
};

const classStatusColors: Record<string, string> = {
  in_progress: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  pending_approval: "bg-orange-100 text-orange-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
  invested: "bg-yellow-100 text-yellow-800",
};

const classPinImages: Record<string, string> = {
  Amigo: "/images/class/amigo.jpg",
  "Amigo Avanzado": "/images/class/amigo.jpg",
  Compañero: "/images/class/companero.jpg",
  "Compañero Avanzado": "/images/class/companero.jpg",
  Explorador: "/images/class/explorador.jpg",
  "Explorador Avanzado": "/images/class/explorador.jpg",
  Orientador: "/images/class/orientador.jpg",
  "Orientador Avanzado": "/images/class/orientador.jpg",
  Viajero: "/images/class/viajero.jpg",
  "Viajero Avanzado": "/images/class/viajero.jpg",
  Guía: "/images/class/guia.jpg",
  "Guía Avanzado": "/images/class/guia.jpg",
};

export default function MemberProgressModal({
  isOpen,
  onClose,
  member,
  clubId,
  periodId,
  onAddAchievement,
  onAddClass,
}: MemberProgressModalProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["achievements", "classes"]),
  );
  const [selectedAchievement, setSelectedAchievement] =
    useState<Achievement | null>(null);
  const [selectedClassProgress, setSelectedClassProgress] =
    useState<ClassProgress | null>(null);

  // Obtener logros y progreso del miembro
  const { data: achievements, isLoading: loadingAchievements } =
    useAchievementsByPerson(member.personId || member.id);
  const { data: classProgress, isLoading: loadingClasses } =
    useClassProgressByPerson(member.personId || member.id);

  // Catálogos para imágenes
  const { data: specialties } = useSpecialties();
  const { data: maestrias } = useMaestrias();
  const { data: certificaciones } = useCertificaciones();

  // Mapas de catálogos
  const specialtyMap = useMemo(() => {
    const map = new Map<string, { categorySlug: string }>();
    specialties?.forEach((s) =>
      map.set(s.id, { categorySlug: s.categorySlug || "" }),
    );
    return map;
  }, [specialties]);

  const maestriaMap = useMemo(() => {
    const map = new Map<string, { localImage: string }>();
    maestrias?.forEach((m) =>
      map.set(m.id, { localImage: m.localImage || "" }),
    );
    return map;
  }, [maestrias]);

  const certificacionMap = useMemo(() => {
    const map = new Map<string, { localImage: string }>();
    certificaciones?.forEach((c) =>
      map.set(c.id, { localImage: c.localImage || "" }),
    );
    return map;
  }, [certificaciones]);

  // Agrupar logros por tipo
  const achievementsByType = useMemo(() => {
    const grouped: Record<string, Achievement[]> = {
      specialty: [],
      mastery: [],
      certification: [],
    };
    achievements?.forEach((a) => {
      if (grouped[a.type]) {
        grouped[a.type].push(a);
      }
    });
    return grouped;
  }, [achievements]);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "-";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const memberName =
    member.fullName ||
    `${member.firstName || ""} ${member.lastName || ""}`.trim();
  const totalAchievements = achievements?.length || 0;
  const totalClasses = classProgress?.length || 0;

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-in fade-in zoom-in-95 duration-200">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              {member.photoUrl ? (
                <img
                  src={member.photoUrl}
                  alt={memberName}
                  className="w-14 h-14 rounded-xl object-cover shadow-md ring-2 ring-white"
                />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center shadow-md shadow-primary-500/25">
                  <User className="w-7 h-7 text-white" />
                </div>
              )}
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {memberName}
                </h2>
                <div className="flex gap-2 mt-1">
                  <Badge className="bg-purple-100 text-purple-700">
                    {totalAchievements} logros
                  </Badge>
                  <Badge className="bg-blue-100 text-blue-700">
                    {totalClasses} clases
                  </Badge>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Sección de Logros (Especialidades, Maestrías, Certificaciones) */}
            <div className="border-2 border-gray-100 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection("achievements")}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center shadow-md shadow-purple-500/25">
                    <Award className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900">
                      Especialidades, Maestrías y Certificaciones
                    </h3>
                    <p className="text-sm font-medium text-gray-500">
                      {totalAchievements} registrados
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddAchievement();
                    }}
                    className="shadow-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Agregar
                  </Button>
                  {expandedSections.has("achievements") ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>

              {expandedSections.has("achievements") && (
                <div className="border-t">
                  {loadingAchievements ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                    </div>
                  ) : totalAchievements === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <div className="w-12 h-12 bg-gradient-to-br from-gray-200 to-gray-300 rounded-xl flex items-center justify-center mx-auto mb-2">
                        <Award className="w-6 h-6 text-gray-400" />
                      </div>
                      <p>No hay logros registrados</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {/* Especialidades */}
                      {achievementsByType.specialty.length > 0 && (
                        <div className="p-4">
                          <h4 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                            <Award className="w-4 h-4 text-purple-600" />
                            Especialidades (
                            {achievementsByType.specialty.length})
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {achievementsByType.specialty.map((a) => {
                              const info = specialtyMap.get(a.catalogId);
                              return (
                                <div
                                  key={a.id}
                                  onClick={() => setSelectedAchievement(a)}
                                  className="flex items-center gap-3 p-3 bg-gradient-to-r from-purple-50 to-white rounded-xl cursor-pointer hover:from-purple-100 hover:shadow-md transition-all duration-200 group border border-purple-100"
                                >
                                  <SpecialtyImage
                                    name={a.catalogName}
                                    categorySlug={info?.categorySlug || ""}
                                    size="sm"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 truncate">
                                      {a.catalogName}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {formatDate(a.earnedDate)}
                                    </p>
                                  </div>
                                  <Edit2 className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Maestrías */}
                      {achievementsByType.mastery.length > 0 && (
                        <div className="p-4">
                          <h4 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                            <GraduationCap className="w-4 h-4 text-amber-600" />
                            Maestrías ({achievementsByType.mastery.length})
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {achievementsByType.mastery.map((a) => {
                              const info = maestriaMap.get(a.catalogId);
                              return (
                                <div
                                  key={a.id}
                                  onClick={() => setSelectedAchievement(a)}
                                  className="flex items-center gap-3 p-3 bg-gradient-to-r from-amber-50 to-white rounded-xl cursor-pointer hover:from-amber-100 hover:shadow-md transition-all duration-200 group border border-amber-100"
                                >
                                  <img
                                    src={
                                      info?.localImage ||
                                      "/images/specialties/placeholder.png"
                                    }
                                    alt={a.catalogName}
                                    className="w-10 h-10 rounded-xl object-contain bg-white"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).src =
                                        "/images/specialties/placeholder.png";
                                    }}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 truncate">
                                      {a.catalogName}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {formatDate(a.earnedDate)}
                                    </p>
                                  </div>
                                  <Edit2 className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Certificaciones */}
                      {achievementsByType.certification.length > 0 && (
                        <div className="p-4">
                          <h4 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                            <Medal className="w-4 h-4 text-cyan-600" />
                            Certificaciones (
                            {achievementsByType.certification.length})
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {achievementsByType.certification.map((a) => {
                              const info = certificacionMap.get(a.catalogId);
                              return (
                                <div
                                  key={a.id}
                                  onClick={() => setSelectedAchievement(a)}
                                  className="flex items-center gap-3 p-3 bg-gradient-to-r from-cyan-50 to-white rounded-xl cursor-pointer hover:from-cyan-100 hover:shadow-md transition-all duration-200 group border border-cyan-100"
                                >
                                  <img
                                    src={
                                      info?.localImage ||
                                      "/images/specialties/placeholder.png"
                                    }
                                    alt={a.catalogName}
                                    className="w-10 h-10 rounded-xl object-contain bg-white"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).src =
                                        "/images/specialties/placeholder.png";
                                    }}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 truncate">
                                      {a.catalogName}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {formatDate(a.earnedDate)}
                                    </p>
                                  </div>
                                  <Edit2 className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sección de Clases Progresivas */}
            <div className="border-2 border-gray-100 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection("classes")}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center shadow-md shadow-green-500/25">
                    <Trophy className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900">
                      Clases Progresivas
                    </h3>
                    <p className="text-sm font-medium text-gray-500">
                      {totalClasses} registradas
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddClass();
                    }}
                    className="shadow-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Agregar
                  </Button>
                  {expandedSections.has("classes") ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>

              {expandedSections.has("classes") && (
                <div className="border-t">
                  {loadingClasses ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                    </div>
                  ) : totalClasses === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <div className="w-12 h-12 bg-gradient-to-br from-gray-200 to-gray-300 rounded-xl flex items-center justify-center mx-auto mb-2">
                        <Trophy className="w-6 h-6 text-gray-400" />
                      </div>
                      <p>No hay clases registradas</p>
                    </div>
                  ) : (
                    <div className="p-4 space-y-2">
                      {classProgress?.map((cp) => {
                        const pinImage = classPinImages[cp.className];
                        return (
                          <div
                            key={cp.id}
                            onClick={() => setSelectedClassProgress(cp)}
                            className="flex items-center gap-3 p-3 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl cursor-pointer hover:from-green-100 hover:to-emerald-100 transition-all duration-300 group border border-green-100"
                          >
                            {pinImage ? (
                              <img
                                src={pinImage}
                                alt={cp.className}
                                className="w-10 h-10 rounded-xl object-cover shadow-md"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-md">
                                <Trophy className="w-5 h-5 text-white" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900">
                                {cp.className}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <div className="flex-1 max-w-[120px] bg-gray-200 rounded-full h-1.5">
                                  <div
                                    className="bg-green-500 h-1.5 rounded-full"
                                    style={{
                                      width: `${cp.completionPercentage}%`,
                                    }}
                                  />
                                </div>
                                <span className="text-xs text-gray-500">
                                  {cp.completionPercentage}%
                                </span>
                              </div>
                            </div>
                            <Badge className={classStatusColors[cp.status]}>
                              {classStatusLabels[cp.status]}
                            </Badge>
                            <Edit2 className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gradient-to-r from-gray-50 to-white border-t border-gray-100 px-6 py-4 flex justify-end">
            <Button variant="secondary" onClick={onClose} className="shadow-sm">
              Cerrar
            </Button>
          </div>
        </div>
      </div>

      {/* Modal de edición de achievement */}
      {selectedAchievement && (
        <EditAchievementModal
          isOpen={!!selectedAchievement}
          achievement={selectedAchievement}
          onClose={() => setSelectedAchievement(null)}
        />
      )}

      {/* Modal de edición de clase */}
      {selectedClassProgress && (
        <ClassProgressModal
          isOpen={!!selectedClassProgress}
          clubId={clubId}
          periodId={periodId}
          classProgress={selectedClassProgress}
          onClose={() => setSelectedClassProgress(null)}
        />
      )}
    </>
  );
}
